import React, {
  useState,
  InputHTMLAttributes,
  useCallback,
  useRef,
} from "react";
import { useApiRequest } from "../helper/api";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { matchingNameResponse } from "../model/projectModel";

interface ReusableInputProps extends InputHTMLAttributes<HTMLInputElement> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setOptionsData?: ((item: matchingNameResponse | null) => void) | undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DebounceFunc<T extends (...args: any[]) => void> = (
  func: T,
  delay: number
) => (...args: Parameters<T>) => void;

const debounce: DebounceFunc<(searchQuery: string) => void> = (func, delay) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<typeof func>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const ReusableSearchInput: React.FC<ReusableInputProps> = ({
  value,
  onChange,
  setOptionsData,
  ...rest
}) => {
  const [options, setOptions] = useState<matchingNameResponse[] | null>(null);
  const { fetchsearchNameData } = useApiRequest();
  const [loading, setLoading] = React.useState(false);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchOptions = useCallback(
    debounce(async (searchQuery: string) => {
      setLoading(true);
      try {
        const response = await fetchsearchNameData(
          "/manage/get_matching_names",
          {
            method: "POST",
            data: {
              search_string: searchQuery,
            },
          }
        );
        if (response?.status) {
          setOptions(response.data);
          setShowOptions(true);
        } else {
          setOptions(null);
          setOptionsData?.(null);
          setShowOptions(false);
        }
      } catch (error) {
        console.error("Error fetching options:", error);
      } finally {
        setLoading(false);
      }
    }, 400), // Adjust debounce time here (e.g., 400ms)
    [fetchsearchNameData, setLoading]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent API call on certain keydown events (e.g., Arrow keys, Tab)
    const key = e.key;
    // console.log(key);
    if (key === " ") {
      fetchOptions(value);
      setShowOptions(true);
    }
    // if (key === "ArrowDown" || key === "ArrowUp" || key === "Tab") {
    //   setShowOptions(!showOptions);
    // }
  };

  return (
    <div
      className="max-w-md mx-auto"
      onMouseLeave={() => setShowOptions(false)}
    >
      <div className="relative w-full">
        <div onClick={() => setShowOptions(true)}>
          <input
            type="search"
            value={value}
            onChange={(e) => {
              onChange(e);
            }}
            onKeyDown={handleKeyDown}
            {...rest}
            ref={inputRef}
            className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
            placeholder="Search name"
            autoComplete="off"
          />
        </div>
        {loading ? "searching ..." : ""}
        {showOptions && options && (
          <ul
            className="absolute bg-white border border-gray-300 text-gray-900 text-sm rounded-lg w-full max-h-60 overflow-y-auto z-10"
            style={{ top: "100%" }}
          >
            {options.length > 0 ? (
              options
                .filter((item) =>
                  item.ldap_uid.toLowerCase().includes(value.toLowerCase())
                )
                .map((option, index) => (
                  <li
                    key={index}
                    className={`p-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center ${
                      option.status == 1 ? " text-green-800" : " text-red-800"
                    }`}
                    onClick={() => {
                      if (inputRef.current) {
                        inputRef.current.value = option.ldap_uid;
                        setOptionsData?.(option);
                        setShowOptions(false);
                      }
                    }}
                  >
                    <span>{option.ldap_uid}</span>
                    <span
                      className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${
                        option.status == 1
                          ? "bg-green-200 text-green-800"
                          : "bg-red-200 text-red-800"
                      }`}
                    >
                      {option.status == 1 ? "" : ""}
                    </span>
                  </li>
                ))
            ) : (
              <li className="p-2 hover:bg-gray-100 cursor-pointer">
                no record
              </li>
            )}
          </ul>
        )}
        <button
          type="button"
          disabled={rest.disabled}
          onClick={() => {
            fetchOptions(value);
          }}
          className="text-white absolute right-1 bottom-1 bg-blue-300 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-xs p-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
        >
          <MagnifyingGlassIcon className="h-5 w-5 text-white" />
        </button>
      </div>
    </div>
  );
};

export default ReusableSearchInput;
